import { useState, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { AnimatedCard } from "./AnimatedCard"
import { TierBadge } from "./TierBadge"
import { useApexState } from "@/hooks/useApexState"
import { 
  Zap, 
  Crown,
  Trophy,
  Heart,
  ShoppingBag,
  Users,
  Star,
  X,
  ChevronDown
} from "lucide-react"

interface LiveActivityItem {
  id: string
  type: 'purchase' | 'achievement' | 'social' | 'challenge' | 'tier_upgrade' | 'drop_alert'
  user?: {
    name: string
    username: string
    avatar: string
    tier: 'initiate' | 'creator' | 'architect'
  }
  content: {
    title: string
    description: string
    metadata?: any
  }
  timestamp: string
  isNew: boolean
  priority: 'low' | 'medium' | 'high'
}

interface LiveActivityProps {
  className?: string
  isMinimized?: boolean
  onToggle?: () => void
}

const mockActivities: LiveActivityItem[] = [
  {
    id: "1",
    type: "drop_alert",
    content: {
      title: "🔥 Architect Collection Live!",
      description: "Limited pieces now available. Only 47 left in stock.",
      metadata: { itemCount: 47, originalCount: 100 }
    },
    timestamp: "Just now",
    isNew: true,
    priority: "high"
  },
  {
    id: "2",
    type: "tier_upgrade",
    user: {
      name: "Jordan Kim",
      username: "@jordan_urban",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop",
      tier: "architect"
    },
    content: {
      title: "🎉 New Architect Member!",
      description: "Jordan just unlocked Architect tier with their latest challenge win.",
    },
    timestamp: "2m ago",
    isNew: true,
    priority: "medium"
  },
  {
    id: "3",
    type: "purchase",
    user: {
      name: "Maya Rodriguez",
      username: "@maya_moves",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b67e1c04?w=40&h=40&fit=crop",
      tier: "architect"
    },
    content: {
      title: "💫 Exclusive Purchase",
      description: "Maya just secured the Limited Edition Architect Hoodie.",
      metadata: { productName: "Architect Hoodie", price: 220 }
    },
    timestamp: "5m ago",
    isNew: false,
    priority: "medium"
  },
  {
    id: "4",
    type: "challenge",
    content: {
      title: "⚡ Challenge Update",
      description: "Motion & Form challenge ends in 4 hours. 23 new submissions today!",
      metadata: { submissionCount: 23, timeLeft: "4 hours" }
    },
    timestamp: "12m ago",
    isNew: false,
    priority: "medium"
  },
  {
    id: "5",
    type: "social",
    user: {
      name: "Alex Chen",
      username: "@alex_shapes",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop",
      tier: "creator"
    },
    content: {
      title: "💖 Community Love",
      description: "Alex's mindful movement post just hit 100 likes!",
      metadata: { postType: "movement", likes: 100 }
    },
    timestamp: "18m ago",
    isNew: false,
    priority: "low"
  },
  {
    id: "6",
    type: "achievement",
    user: {
      name: "Sarah Kim",
      username: "@sarah_style",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b67e1c04?w=40&h=40&fit=crop",
      tier: "architect"
    },
    content: {
      title: "🏆 Style Master Unlocked",
      description: "Sarah completed 10 style challenges and earned the Style Master badge.",
      metadata: { achievementType: "Style Master", challengeCount: 10 }
    },
    timestamp: "25m ago",
    isNew: false,
    priority: "medium"
  }
]

export function LiveActivity({ className, isMinimized = false, onToggle }: LiveActivityProps) {
  const { state } = useApexState()
  const [activities, setActivities] = useState<LiveActivityItem[]>(mockActivities)
  const [isExpanded, setIsExpanded] = useState(!isMinimized)

  // Simulate live updates
  useEffect(() => {
    const interval = setInterval(() => {
      // Randomly add new activities
      if (Math.random() > 0.7) {
        const newActivity: LiveActivityItem = {
          id: `live-${Date.now()}`,
          type: "social",
          user: {
            name: "Community Member",
            username: "@member",
            avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=40&h=40&fit=crop",
            tier: "creator"
          },
          content: {
            title: "✨ New Activity",
            description: "Someone just joined a challenge!",
          },
          timestamp: "Just now",
          isNew: true,
          priority: "low"
        }

        setActivities(prev => [newActivity, ...prev.slice(0, 9)])
      }

      // Mark activities as not new after 5 minutes
      setActivities(prev => 
        prev.map(activity => ({
          ...activity,
          isNew: activity.timestamp === "Just now" ? false : activity.isNew
        }))
      )
    }, 15000) // Update every 15 seconds

    return () => clearInterval(interval)
  }, [])

  const getActivityIcon = (type: LiveActivityItem['type']) => {
    switch (type) {
      case 'purchase':
        return <ShoppingBag size={16} className="text-primary" />
      case 'achievement':
        return <Trophy size={16} className="text-accent" />
      case 'social':
        return <Heart size={16} className="text-red-400" />
      case 'challenge':
        return <Zap size={16} className="text-yellow-500" />
      case 'tier_upgrade':
        return <Crown size={16} className="text-tier-architect" />
      case 'drop_alert':
        return <Star size={16} className="text-primary" />
      default:
        return <Zap size={16} className="text-muted-foreground" />
    }
  }

  const getPriorityStyle = (priority: LiveActivityItem['priority'], isNew: boolean) => {
    const baseClasses = "transition-all duration-200"
    const priorityClasses = {
      high: "border-l-primary bg-primary/5",
      medium: "border-l-accent bg-accent/5", 
      low: "border-l-muted bg-muted/5"
    }
    
    return `${baseClasses} ${priorityClasses[priority]} ${isNew ? 'animate-pulse-gold' : ''}`
  }

  const newActivityCount = activities.filter(a => a.isNew).length

  return (
    <div className={`fixed top-20 right-4 z-30 w-80 ${className}`}>
      <AnimatedCard>
        <Card className="bg-card/90 backdrop-blur-sm border-border shadow-lg">
          {/* Header */}
          <div 
            className="flex items-center justify-between p-4 cursor-pointer"
            onClick={() => {
              setIsExpanded(!isExpanded)
              onToggle?.()
            }}
          >
            <div className="flex items-center gap-2">
              <div className="relative">
                <Zap size={16} className="text-primary" />
                {newActivityCount > 0 && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full animate-pulse" />
                )}
              </div>
              <h3 className="font-semibold text-foreground">Live Activity</h3>
              {newActivityCount > 0 && (
                <Badge className="bg-primary text-black text-xs">
                  {newActivityCount}
                </Badge>
              )}
            </div>
            
            <ChevronDown 
              size={16} 
              className={`text-muted-foreground transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          </div>

          {/* Activities List */}
          {isExpanded && (
            <CardContent className="p-0 border-t border-border max-h-96 overflow-y-auto">
              <div className="space-y-1">
                {activities.slice(0, 8).map(activity => (
                  <div
                    key={activity.id}
                    className={`p-3 border-l-2 hover:bg-white/5 cursor-pointer ${getPriorityStyle(activity.priority, activity.isNew)}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getActivityIcon(activity.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium text-foreground leading-tight">
                            {activity.content.title}
                          </p>
                          {activity.isNew && (
                            <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                          )}
                        </div>
                        
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {activity.content.description}
                        </p>
                        
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-muted-foreground">
                            {activity.timestamp}
                          </span>
                          
                          {activity.user && (
                            <div className="flex items-center gap-1">
                              <Avatar className="w-4 h-4">
                                <AvatarImage src={activity.user.avatar} />
                                <AvatarFallback className="text-xs">{activity.user.name.slice(0, 1)}</AvatarFallback>
                              </Avatar>
                              <TierBadge tier={activity.user.tier} size="xs" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {activities.length > 8 && (
                <div className="p-3 text-center border-t border-border">
                  <Button variant="ghost" size="sm" className="text-xs">
                    View All Activity
                  </Button>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </AnimatedCard>
    </div>
  )
}

// Hook for managing live activity state
export function useLiveActivity() {
  const [isVisible, setIsVisible] = useState(true)
  const [isMinimized, setIsMinimized] = useState(false)

  const toggle = () => setIsMinimized(!isMinimized)
  const show = () => setIsVisible(true)
  const hide = () => setIsVisible(false)

  return {
    isVisible,
    isMinimized,
    toggle,
    show,
    hide
  }
}